import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { Outlet } from "react-router-dom";
import i18next from "i18next";
import { useU2000InitLUHandlers } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { U2000HandleApiError, type U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { doInitLU, luHandleAppiError } from "./U2104Service";
import { U2104ContextProvider } from "./U2104Context";
import { useU2000LogicalUnitContext } from "../u2000Core/context/logicalUnit/U2000LogicalUnitContext";

// Namespace constant and roles definition
export const NS = "u2104";

// Definition des rôles pour l'unité logique U2104
export const U2104ROLES = {
  READ: "U2104:READ",
  ADD: "U2104:ADD",
  EDIT: "U2104:EDIT",
  DELETE: "U2104:DELETE",
  AUDITS: "U2104:AUDITS",
  ROLES: "U2104:ROLES",
  USERS: "U2104:USERS",
  EXPORT: "U2104:EXPORT",
} as const;
export type U2104ROLES = (typeof U2104ROLES)[keyof typeof U2104ROLES];

// Definition of the initialization parameters for the logical unit U2104
export interface U2104InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
}

// Main component for the U2104 logical unit
const U2104: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U2104InitLU>({ launchSearch: false, hideSearch: true, nbRecordPerPage: 50 });

  // charge fichiers de traduction
  useEffect(() => {
    i18next.loadNamespaces(["u2104"], (err) => {
      if (err) return console.error("Error loading namespace:", err);
      setI18nReady(true);
    });
  }, [i18n]);

  // Gestion de l'initialisation de l'unité logique
  const handleInitLU = useU2000InitLUHandlers<U2104InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U2104InitLU) => {
      setInitLUData(initLU);
      setInitLUReady(true);
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  // Initialisation de l'unité logique au montage du composant et apres quelques changements de langue
  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]);

  // Set LU title and breadcrumb
  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    const title = t("title", { ns: NS });
    setLogicalUnitTitle(title);
    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  return (
    <U2104ContextProvider initLU={initLUData}>
      <Outlet />
    </U2104ContextProvider>
  );
};

export default U2104;
